<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import customerInstallingIndowWindowInserts from './images/customerInstallingIndowWindowInserts.png';
	import largeWindowWithSeat from './images/largeWindowWithSeat.jpg';
	import manLookingOutWindow from './images/manLookingOutWindow.jpg';
	import rainAgainstWindow from './images/rainAgainstWindow.jpg';
	import windowWithFlowersOnWindowSill from './images/windowWithFlowersOnWindowSill.jpg';
	import womanInstallingWindowInserts from './images/womanInstallingWindowInserts.jpg';
</script>

<Section>
	<Image
		alt="A second-story room with an old window looking out to a backyard. There are curtains hanging from the sides and a ledge by the window holding a plant and some books."
		src={largeWindowWithSeat}
		class="w-full"
	>
		{#snippet descriptionSlot()}
			<a
				href="https://unsplash.com/photos/Fd9tUmRBJzk"
				target="_blank"
				rel="external">Source</a
			>
		{/snippet}
	</Image>
	<p>
		When it comes to renovating a home to make it more energy-efficient, it can
		sometimes be hard to determine which projects will be most valuable.
	</p>
	<p>
		Some projects, like replacing windows, may initially seem like an easy way
		to improve the energy performance of a home with some added benefits like
		soundproofing, but replacing windows can quickly become a costly investment
		that may not be worth the minimal return.
	</p>
	<p>
		Fortunately, there may be more cost-effective alternatives, like window
		insert replacements, that provide the same benefits for a fraction of the
		time and money.
	</p>
</Section>

<Section>
	<h2>Full Window Replacement – Is It Worth It?</h2>
	<Image
		alt="A person is standing inside of a house, staring out a large white window, looking out onto the neighborhood street."
		src={manLookingOutWindow}
		class="w-full"
	>
		{#snippet descriptionSlot()}
			<a
				href="https://unsplash.com/photos/jo8iiwArHfM"
				target="_blank"
				rel="external"
			>
				Source
			</a>
		{/snippet}
	</Image>
	<p>
		A full window replacement is when you remove an existing window down to the
		studs and a new window is installed in its place, including installing and
		painting a new frame and trim. By comparison, a window insert replacement is
		when you install a window pane into your existing window’s frame. This
		window insert fits tightly into your existing window frame without
		disturbing the original frame, exterior trim, or interior casing.
	</p>
	<p>
		If the windows are not salvageable, it may be time to fully replace all the
		windows in a home. Most newer model windows have been developed to
		significantly reduce heat loss and improve a home’s energy efficiency.
		(However, if you have a historic home or need to preserve the original
		windows, it’s best to keep the original windows and find other ways to
		increase energy efficiency.)
	</p>
	<p>
		In 2021, <a
			href="https://www.remodeling.hw.net/cost-vs-value/window-replacement-vinyl-upscale?y=2021"
			target="_blank"
			rel="external">the cost to replace</a
		> 10 existing 3×5-foot double-hung windows with insulated, low-E, simulated-divided-light
		vinyl windows with a custom-color exterior finish was $19,385.
	</p>
	<p>
		In order to completely insulate the home, every single window needs to be
		replaced. This quickly becomes a very expensive commitment with money that
		would most likely have a bigger impact elsewhere. And, it’s unlikely the
		money spent would ever be recouped through energy savings — at least not for
		several decades, and most replacement window warranties do not last that
		long.
	</p>
	<p>
		Replacing windows is not an amateur DIY project that can be easily done over
		a weekend. The only way to ensure the windows are installed properly and
		provide the best insulation possible is to hire a professional crew, which
		does not come cheap.
	</p>
	<Image
		alt="Two windows have white painted panes with a window lock on the bottom half of the window. There are two plants sitting on the window sill and a teapot in between them."
		src={windowWithFlowersOnWindowSill}
		class="w-full"
	>
		{#snippet descriptionSlot()}
			<a
				href="https://unsplash.com/photos/U8zsjmKA840"
				target="_blank"
				rel="external">Source</a
			>
		{/snippet}
	</Image>
	<p>
		When renovating an older home to achieve a more contemporary design
		aesthetic, replacing outdated windows with a more modern style can update
		the look of both the interior and exterior of the home.
	</p>
	<p>
		However, if the homeowner only wants to replace a few windows and is looking
		for replacements to match existing windows in the home, this may be more of
		a challenge. Models change with time and technology advances, so finding the
		very same window used before may not be possible.
	</p>
	<p>
		Also, in some historic or custom homes, the windows may not be able to be
		changed at all. Windows specifically crafted for the home with stained glass
		simply cannot be replaced without risking the original integrity and charm
		of the space — or even be allowed if the home is designated as a historic
		property.
	</p>
	<p>
		However, if the home is situated on a busy corner or next door to some
		particularly noisy neighbors, the <a href="/resources/soundproofing-windows"
			>noise dampening benefits</a
		> of modern windows may seem appealing enough to rip out the old and invest heavily
		in the new.
	</p>
	<p>
		However, it’s worth considering the alternatives before making such a costly
		decision.
	</p>
</Section>

<Section>
	<h2>What Are the Alternatives?</h2>
	<p>
		Advancements in technologies have drastically improved the performance of
		modern windows, but quality replacement window alternatives have also become
		readily available.
	</p>
	<p>
		These alternatives are much more time and cost-effective than replacing old
		windows while still offering the same energy savings and <a
			href="/resources/blog/soundproof-windows-home-depot">noise dampening</a
		> benefits.
	</p>
	<p>
		Here are a few of the available replacement alternatives and why they may be
		the better option for any home.
	</p>
	<h3>Exterior Storm Windows</h3>
	<Image
		alt="A close-up of raindrops dripping down the bottom of a window."
		class="w-full"
		src={rainAgainstWindow}
	>
		{#snippet descriptionSlot()}
			<a
				href="https://unsplash.com/photos/gt204d69QXs"
				target="_blank"
				rel="external">Source</a
			>
		{/snippet}
	</Image>
	<p>
		If the home is located in an area known for its adverse weather conditions
		and the existing windows could use an extra level of protection, exterior
		storm windows are a good option — especially on a tight budget.
	</p>
	<p>
		Exterior storm windows are installed on the exterior of the house, outside
		of the preexisting windows. The primary function of storm windows is to
		provide extra wind and weather protection. They also provide an extra layer
		of insulation to normalize interior temperature, and they can help reduce
		noise.
	</p>
	<p>
		They are much easier to install than replacement windows, as they simply
		screw into the exterior of the house and do not require an expert to
		install. And since they are installed on the outside of the home, exterior
		storm windows do not change the interior aesthetics — though they will
		change your exterior aesthetics.
	</p>
	<p>
		However, exterior storm windows require a lot of maintenance and are
		difficult to clean thoroughly without unscrewing and can trap dirt and
		moisture against the interior window.
	</p>
	<p>
		Poor quality exterior storm windows often lead to air leakage and little to
		no added insulation. So, if energy efficiency is important, this may not be
		the best option.
	</p>
	<p>
		Also, while simple to install on a single-story home, reaching upper levels
		is difficult and could lead to an insufficient installation.
	</p>
	<h3>Window Inserts</h3>
	<Image
		alt="A woman is pushing the bottom right corner of a window insert replacement into place."
		src={womanInstallingWindowInserts}
	>
		{#snippet descriptionSlot()}
			<a href="/solutions/window-noise-reduction" target="_blank" rel="external"
				>Source</a
			>
		{/snippet}
	</Image>
	<p>
		Window inserts are fully functional secondary windows that work within the
		existing frames to seal drafts, reduce noise, and some even filter dangerous
		UV rays.
	</p>
	<p>
		<a href="/solutions/window-insulation">Replacement window inserts</a> are easy
		to install and do not need extensive mounting hardware or professional help. Indow
		uses its patented silicone compression Tube to keep window inserts in place instead
		of hardware like tracking. This saves your window frames from damage, keeping
		them beautiful for years to come. The low-profile design means they virtually
		disappear once installed. If maintaining the integrity of a historic site is important,
		window inserts are the best option.
	</p>
	<p>
		Window inserts regulate the temperature year-round for optimal energy
		savings, and they are easy to maintain since they do not need to be
		unscrewed to deep clean.
	</p>
	<p>
		There are also different types of inserts that offer a variety of
		light-blocking capabilities. So, whether the goal is to block out UV rays,
		diffuse the light for a little privacy, or totally blackout a room for the
		optimal sleeping environment, there is a specific insert available.
	</p>
	<p>
		And if <a href="/solutions/window-noise-reduction">noise cancellation</a> is the
		most important factor, inserts can reduce unwanted noise by up to 70%* — incredible,
		right?
	</p>
	<h3>Interior Insulating Blinds</h3>
	<p>
		Interior insulating blinds are a type of window treatment designed to
		maximize energy efficiency. They are made of layers of pleated fabric
		materials that fold up accordion-style usually to the top of the window.
	</p>
	<p>
		The pleated layers create a “honeycomb” effect that acts as a highly
		effective insulator to reduce heat loss in the winter and unwanted solar
		heat in the summer.
	</p>
	<p>
		Interior insulating blinds come in a variety of colors, textures, and
		patterns to fit into any design aesthetic. Some even include automation and
		can be controlled by a remote or through a preplanned schedule.
	</p>
	<p>
		These window treatments, however, work by covering the windows, which may
		not be the desirable outcome when dealing with historic or custom window
		features like stained glass. They also do not help air leakage from broken
		or drafty windows.
	</p>
</Section>

<Section>
	<h2>How Can Indow Help?</h2>
	<Image
		alt="A person is installing a large window insert while wearing a grey sweater and dark pants."
		src={customerInstallingIndowWindowInserts}
		class="w-80"
	>
		{#snippet source()}
			<a href="/case-studies/block-cold-from-windows">Source</a>
		{/snippet}
	</Image>
	<p>
		Indow offers custom, compression-fit window inserts that <a
			href="/resources/case-studies">fit the needs of every home</a
		>.
	</p>
	<p>
		We help homeowners by offering increased energy savings, light and UV
		protection, and soundproofing without having to devote serious money and
		time to replace all their windows.
	</p>
	<p>
		Our products perform best when you don’t even realize they are there. Our
		window inserts will make any space more comfortable and functional while
		staying invisible, allowing the true design of the room to stand out.
	</p>
	<p>
		So, if you are a homeowner looking to improve the performance of your home
		and overall quality of life, check out the <a href="/"
			>range of window inserts</a
		>
		Indow has to offer and get your
		<a href="/how-to/get-indow-inserts">free estimate</a> today.
	</p>
	<p class="text-left text-sm italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full Noise Reduction Sheet to
		learn more.
	</p>
</Section>

<Reviews />
